import React from 'react'
import { Card, Spin } from '@surecompanies/core_components'
import {
  FileTextOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import { PaymentAssignmentStatusId, PaymentCase } from './Utils/types'
import { PaymentAssignmentRole } from './Utils/roles'
import type { TaskSummary } from '../../api/dto/get-payment-assignment-task-summary.dto'
import { isCheckerSummary } from '../../api/dto/get-payment-assignment-task-summary.dto'

const ScoreboardContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`

const MetricCardWrapper = styled.div`
  flex: 1;
  min-width: 200px;
  cursor: pointer;
  transition: all 0.3s ease;

  .ant-card {
    border: 1px solid #d6d8d9;
    height: 100%;
  }

  &:hover {
    .ant-card {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  }
`

const MetricContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const MetricIcon = styled.div`
  font-size: 24px;
  color: #3a4951;
`

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #3a4951;
  line-height: 1;
`

const MetricLabel = styled.div`
  font-size: 14px;
  color: #677786;
  font-weight: 500;
  margin-top: 4px;
`

interface ScoreboardProps {
  cases: PaymentCase[]
  totalCases?: number
  onMetricClick: (filterType: string) => void
  currentUserId?: number
  role: PaymentAssignmentRole
  selectedAssigneeIds?: number[]
  /** When provided, metrics use task-summary API data instead of deriving from cases. */
  taskSummary?: TaskSummary | null
  taskSummaryLoading?: boolean
}

export default function Scoreboard({
  cases,
  totalCases,
  onMetricClick,
  currentUserId,
  role,
  selectedAssigneeIds,
  taskSummary,
  taskSummaryLoading,
}: ScoreboardProps) {
  // onMetricClick already triggers refresh in the parent — no extra fire needed here.
  const handleMetricApply = (filterType: string) => {
    onMetricClick(filterType)
  }

  const metrics = React.useMemo(() => {
    const shouldShowMakerView = role === PaymentAssignmentRole.MAKER
    const shouldShowEscalatorView = role === PaymentAssignmentRole.ESCALATOR

    if (taskSummary && !isCheckerSummary(taskSummary)) {
      const s = taskSummary
      if (shouldShowMakerView) {
        const percentageDone =
          s.totalAssignment > 0
            ? (s.totalCompleted / s.totalAssignment) * 100
            : 0
        return {
          remainingTasks: s.totalPending,
          completed: s.totalCompleted,
          totalAssigned: s.totalAssignment,
          percentageDone,
        }
      }
      if (shouldShowEscalatorView) {
        return {
          totalCases: s.totalAssignment,
          pendingSolvingCase: s.totalPending,
          totalEscalationDone: s.totalCompleted,
        }
      }
    }

    if (taskSummary && isCheckerSummary(taskSummary)) {
      const s = taskSummary
      return {
        pendingAssignment: s.pendingAssignment,
        escalatedCases: s.escalatedAssignment,
        totalTodo: s.pendingAssignment + s.escalatedAssignment,
        totalCases: s.totalAssignment,
      }
    }

    const getStatusId = (c: PaymentCase) => c.paymentStatus?.id ?? undefined

    const assigneeIdsFilter =
      Array.isArray(selectedAssigneeIds) && selectedAssigneeIds.length
        ? selectedAssigneeIds
        : undefined

    if (shouldShowMakerView) {
      // Scope maker metrics to:
      // - explicit assigneeIds filter if provided
      // - otherwise current userId (if provided)
      const scopedCases = assigneeIdsFilter
        ? cases.filter(
            (c) =>
              c.assigneeId !== null &&
              c.assigneeId !== undefined &&
              assigneeIdsFilter.includes(c.assigneeId)
          )
        : currentUserId
          ? cases.filter((c) => c.assigneeId === currentUserId)
          : cases

      // Remaining = PENDING_PAYMENT + REATTEMPT + BOT_FAILED; Completed = everything else
      const remainingTasks = scopedCases.filter((c) => {
        const statusId = getStatusId(c)
        return (
          statusId === PaymentAssignmentStatusId.PENDING_PAYMENT ||
          statusId === PaymentAssignmentStatusId.REATTEMPT ||
          statusId === PaymentAssignmentStatusId.BOT_FAILED
        )
      }).length

      const completed = scopedCases.length - remainingTasks

      // Total Assigned is the size of the same scoped set.
      const totalAssigned = scopedCases.length

      // Calculate percentage based on total assigned (not total cases)
      const percentageDone =
        totalAssigned > 0 ? (completed / totalAssigned) * 100 : 0

      return {
        remainingTasks,
        completed,
        totalAssigned,
        percentageDone,
      }
    }

    if (shouldShowEscalatorView) {
      const completedEscalationStatusIds = new Set([
        PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED,
        PaymentAssignmentStatusId.EE_PAID,
        PaymentAssignmentStatusId.TERMINATED_EE,
        PaymentAssignmentStatusId.TERMINATED_PLAN,
        PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE,
        PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT,
      ])

      const totalEscalationDone = cases.filter((c) =>
        (() => {
          const statusId = getStatusId(c)
          return statusId ? completedEscalationStatusIds.has(statusId) : false
        })()
      ).length

      const pendingSolvingCase = cases.length - totalEscalationDone

      return {
        pendingSolvingCase,
        totalEscalationDone,
      }
    }

    // Default to checker metrics
    const pendingAssignmentCases = cases.filter((c) => {
      const statusId = getStatusId(c)
      if (!statusId) return false
      return statusId === PaymentAssignmentStatusId.PENDING_ASSIGNMENT
    })
    const pendingAssignment = pendingAssignmentCases.length

    const escalatedCaseList = cases.filter((c) => {
      const statusId = getStatusId(c)
      if (!statusId) return false
      return (
        statusId === PaymentAssignmentStatusId.PAID_ESCALATION ||
        statusId === PaymentAssignmentStatusId.UNPAID_ESCALATION
      )
    })
    const escalatedCases = escalatedCaseList.length

    const totalTodo = pendingAssignment + escalatedCases

    const allCasesTotal = totalCases ?? cases.length

    return {
      pendingAssignment,
      escalatedCases,
      totalTodo,
      totalCases: allCasesTotal,
    }
  }, [cases, currentUserId, role, selectedAssigneeIds, totalCases, taskSummary])

  const shouldShowMakerView = role === PaymentAssignmentRole.MAKER
  const shouldShowEscalatorView = role === PaymentAssignmentRole.ESCALATOR
  const shouldShowCheckerView = role === PaymentAssignmentRole.CHECKER

  if (taskSummaryLoading && taskSummary == null) {
    return (
      <ScoreboardContainer>
        <MetricCardWrapper style={{ cursor: 'default' }}>
          <Card style={{ backgroundColor: '#ffffff', minHeight: 88 }}>
            <MetricContent>
              <Spin size="small" />
              <MetricLabel>Loading task summary…</MetricLabel>
            </MetricContent>
          </Card>
        </MetricCardWrapper>
      </ScoreboardContainer>
    )
  }

  if (shouldShowMakerView) {
    return (
      <ScoreboardContainer>
        <MetricCardWrapper onClick={() => handleMetricApply('remainingTasks')}>
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <ClockCircleOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.remainingTasks}</MetricValue>
                <MetricLabel>
                  Remaining Tasks
                  {metrics.percentageDone !== undefined && (
                    <span
                      style={{
                        color: '#52c41a',
                        marginLeft: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      ({metrics.percentageDone.toFixed(1)}% done)
                    </span>
                  )}
                </MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>

        <MetricCardWrapper onClick={() => handleMetricApply('completed')}>
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <CheckCircleOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.completed}</MetricValue>
                <MetricLabel>Completed</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>

        <MetricCardWrapper onClick={() => handleMetricApply('totalAssigned')}>
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <UserOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.totalAssigned}</MetricValue>
                <MetricLabel>Total Assigned</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>
      </ScoreboardContainer>
    )
  }

  if (shouldShowEscalatorView) {
    return (
      <ScoreboardContainer>
        <MetricCardWrapper
          onClick={() => handleMetricApply('pendingSolvingCase')}
        >
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <ClockCircleOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.pendingSolvingCase}</MetricValue>
                <MetricLabel>Pending Solving Case</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>

        <MetricCardWrapper
          onClick={() => handleMetricApply('totalEscalationDone')}
        >
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <CheckCircleOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.totalEscalationDone}</MetricValue>
                <MetricLabel>Total Escalation Done</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>

        <MetricCardWrapper onClick={() => handleMetricApply('totalCases')}>
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <FileTextOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.totalCases}</MetricValue>
                <MetricLabel>Total Cases</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>
      </ScoreboardContainer>
    )
  }

  if (shouldShowCheckerView) {
    return (
      <ScoreboardContainer>
        <MetricCardWrapper
          onClick={() => handleMetricApply('pendingAssignment')}
        >
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <ClockCircleOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.pendingAssignment}</MetricValue>
                <MetricLabel>Pending Assignment</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>

        <MetricCardWrapper onClick={() => handleMetricApply('escalatedCases')}>
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <ExclamationCircleOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.escalatedCases}</MetricValue>
                <MetricLabel>Escalated Cases</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>

        <MetricCardWrapper onClick={() => handleMetricApply('totalTodo')}>
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <FileTextOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.totalTodo}</MetricValue>
                <MetricLabel>Total To Do</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>

        <MetricCardWrapper onClick={() => handleMetricApply('totalCases')}>
          <Card style={{ backgroundColor: '#ffffff' }}>
            <MetricContent>
              <MetricIcon>
                <FileTextOutlined />
              </MetricIcon>
              <div>
                <MetricValue>{metrics.totalCases}</MetricValue>
                <MetricLabel>Total Cases</MetricLabel>
              </div>
            </MetricContent>
          </Card>
        </MetricCardWrapper>
      </ScoreboardContainer>
    )
  }

  return null
}
